import {Component, inject, OnInit} from '@angular/core';
import {VivenuService} from "../../../services/vivenu.service";
import {StorageService} from "../../../services/storage.service";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../../../shared/dialog/confirmation-dialog/confirmation-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatOption, MatSelect} from "@angular/material/select";
import {FieldService} from "../../../services/field.service";
import {CustomerService} from "../../../services/customer.service";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MigrationService} from "../../../services/migration.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {EventService} from "../../../services/event.service";

@Component({
  selector: 'app-vivenu-settings',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    DatePipe,
    TranslateModule
  ],
  templateUrl: './vivenu-settings.component.html',
  styleUrl: './vivenu-settings.component.scss'
})
export class VivenuSettingsComponent implements OnInit {

  readonly dialog = inject(MatDialog);
  readonly snackBar = inject(MatSnackBar);
  readonly translate = inject(TranslateService);
  vivenuService: VivenuService = inject(VivenuService);
  storageService: StorageService = inject(StorageService);
  fieldService: FieldService = inject(FieldService);
  customerService: CustomerService = inject(CustomerService);
  migrationService: MigrationService = inject(MigrationService);
  eventService: EventService = inject(EventService);

  vivenu: any;
  migrations: any[] = [];
  latestContactMigration: any = {};
  latestTransactionContactMigration: any = {};
  latestTransactionMigration: any = {};
  apiKeyError: string = '';
  hubspotCustomerId: number | undefined;
  usedBalance = 0;
  loadedBalance = false;

  public vivenuForm = new FormGroup({
    currentVolume: new FormControl({value: 0, disabled: true}),
    vivenuApiKey: new FormControl('', [Validators.required]),
    vivenuSellerId: new FormControl({value: '', disabled: true}, [Validators.required]),
    useOrderObject: new FormControl(true),
    dealPipeline: new FormControl(''),
    dealStageAborted: new FormControl(''),
    dealStagePending: new FormControl(''),
    dealStageComplete: new FormControl(''),
    dealStageProcessed: new FormControl(''),
    dealStageCanceled: new FormControl(''),
    orderPipeline: new FormControl(''),
    orderStageAborted: new FormControl(''),
    orderStagePending: new FormControl(''),
    orderStageComplete: new FormControl(''),
    orderStageProcessed: new FormControl(''),
    orderStageCanceled: new FormControl(''),
    vivenuTransactionCompleteHmacKey: new FormControl(''),
    vivenuCreateContactHmacKey: new FormControl({value: '', disabled: true}),
    vivenuUpdateContactHmacKey: new FormControl({value: '', disabled: true}),
    vivenuCheckoutAbortedHmacKey: new FormControl({value: '', disabled: true}),
    vivenuScanHmacKey: new FormControl({value: '', disabled: true}),
    active: new FormControl(true),
    vivenuCreateContactActive: new FormControl(true),
    vivenuUpdateContactActive: new FormControl(true),
    vivenuTransactionCompleteActive: new FormControl(false),
    vivenuCheckoutAbortedActive: new FormControl(false),
    vivenuScanActive: new FormControl(false),
    anonymizeData: new FormControl(false),
    anonymizeMigrationData: new FormControl(false),
    vivenuTicketsAsObjects: new FormControl(false),
    vivenuTicketsCustomObjectId: new FormControl({value: '', disabled: true}),
    displayLatestOrders: new FormControl(false),
    migrationTimerange: new FormControl('0'),
    migrationDryRun: new FormControl(false),
    displayTicketSeatsInContact: new FormControl(false),
  });

  migrationTimerange = [
    {value: 0, viewValue: 'timerange_all'},
    {value: 720, viewValue: 'timerange_last_thirty_days'},
    {value: 168, viewValue: 'timerange_last_seven_days'},
    {value: 72, viewValue: 'timerange_last_three_days'},
    {value: 24, viewValue: 'timerange_last_day'},
  ];

  hubspotProperties: any;
  filteredHubspotProperties: any;

  vivenuFields: any[] = [];

  ngOnInit() {
    const user = this.storageService.getUser();
    this.hubspotCustomerId = this.storageService.getCustomerIntegrationByType('vivenu').id;
    this.vivenuService.getVivenuCustomer(this.hubspotCustomerId!).subscribe((vivenu: any) => {
      console.log(vivenu);
      this.vivenu = vivenu;
      this.getFields();
      this.vivenuForm.patchValue(vivenu);
      this.vivenu.pipelines.forEach((pipeline: any) => {
        if (pipeline.type === 'order') {
          this.vivenuForm.controls.orderPipeline.setValue(pipeline.id);
          pipeline.stages.forEach((stage: any) => {
            if (stage.type === 'aborted') {
              this.vivenuForm.controls.orderStageAborted.setValue(stage.id);
            } else if (stage.type === 'pending') {
              this.vivenuForm.controls.orderStagePending.setValue(stage.id);
            } else if (stage.type === 'complete') {
              this.vivenuForm.controls.orderStageComplete.setValue(stage.id);
            } else if (stage.type === 'processed') {
              this.vivenuForm.controls.orderStageProcessed.setValue(stage.id);
            } else if (stage.type === 'canceled') {
              this.vivenuForm.controls.orderStageCanceled.setValue(stage.id);
            }
          });
        } else if (pipeline.type === 'deal') {
          this.vivenuForm.controls.dealPipeline.setValue(pipeline.id);
          pipeline.stages.forEach((stage: any) => {
            if (stage.type === 'aborted') {
              this.vivenuForm.controls.dealStageAborted.setValue(stage.id);
            } else if (stage.type === 'pending') {
              this.vivenuForm.controls.dealStagePending.setValue(stage.id);
            } else if (stage.type === 'complete') {
              this.vivenuForm.controls.dealStageComplete.setValue(stage.id);
            } else if (stage.type === 'processed') {
              this.vivenuForm.controls.dealStageProcessed.setValue(stage.id);
            } else if (stage.type === 'canceled') {
              this.vivenuForm.controls.dealStageCanceled.setValue(stage.id);
            }
          });
        }
      });
      this.getMigrations();
    });
    // get events for the customer in the current month so far
    const filterData = {
      hubspotCustomerId: this.hubspotCustomerId,
      integrationId: 'vivenu',
      from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      to: null,
      status: 'processed'
    }
    this.eventService.countEvents(filterData).subscribe({
      next: (result: any) => {
        this.usedBalance = result.count;
        this.loadedBalance = true;
      },
      error: (err: any) => {
        console.error(err);
      }
    });
  }

  getMigrations() {
    this.migrations = [];
    this.latestContactMigration = {};
    this.latestTransactionContactMigration = {};
    this.latestTransactionMigration = {};
    this.migrationService.getMigrations('vivenu', this.hubspotCustomerId!).subscribe((migrations: any) => {
      this.migrations = migrations;
      const latestContactMigrations = migrations.filter((migration: any) => migration.type === 'contacts');
      if (latestContactMigrations && latestContactMigrations.length > 0) {
        this.latestContactMigration = latestContactMigrations.reduce((prev: any, current: any) => (prev.createdAt > current.createdAt) ? prev : current);
      }
      const latestTransactionContactMigration = migrations.filter((migration: any) => migration.type === 'contacts_orders');
      if (latestTransactionContactMigration && latestTransactionContactMigration.length > 0) {
        this.latestTransactionContactMigration = latestTransactionContactMigration.reduce((prev: any, current: any) => (prev.createdAt > current.createdAt) ? prev : current);
      }
      const latestTransactionMigrations = migrations.filter((migration: any) => migration.type === 'orders');
      if (latestTransactionMigrations && latestTransactionMigrations.length > 0) {
        this.latestTransactionMigration = latestTransactionMigrations.reduce((prev: any, current: any) => (prev.createdAt > current.createdAt) ? prev : current);
      }
    });
  }

  getFields() {
    this.fieldService.getFields('vivenu').subscribe({
      next: (fields: any) => {
        this.fieldService.getFieldMappings('vivenu', this.hubspotCustomerId!).subscribe({
          next: (fieldMappings: any) => {
            fields.forEach((field: any) => {
              const mapping = fieldMappings.find((mapping: any) => mapping.fieldId === field.id);
              if (mapping) {
                field.value = mapping.value || field.default;
                field.activeCreate = mapping.activeCreate;
                field.activeUpdate = mapping.activeUpdate;
                field.mappingId = mapping.id;
              } else {
                field.value = field.default;
                field.activeCreate = true;
                field.activeUpdate = true;
              }
              if (field.required) {
                field.activeCreate = true;
                field.activeUpdate = true;
              }
            });
            this.vivenuFields = fields;
          },
          error: err => {
            console.error(err);
          }
        })
      },
      error: err => {
        console.error(err);
      }
    });
    const objectTypes = ['contact', 'deal', 'order', this.vivenu.vivenuTicketsCustomObjectId];
    this.customerService.getAllProperties(this.hubspotCustomerId!, objectTypes).subscribe({
      next: hubspotProperties => {
        // TODO: Add for orders and custom objects
        this.hubspotProperties = hubspotProperties;
        this.filteredHubspotProperties = JSON.parse(JSON.stringify(hubspotProperties));
        console.log(hubspotProperties);
      },
      error: err => {
        console.error(err);
      }
    });
  }

  useApiKey() {
    const apiKey = this.vivenuForm.controls.vivenuApiKey.value!;
    this.vivenuService.useVivenuApiKey(apiKey, this.vivenu.id).subscribe({
      next: (vivenu: any) => {
        console.log(vivenu);
        if (vivenu && vivenu.id) {
          this.vivenu = vivenu;
          this.vivenuForm.patchValue(vivenu);
        } else {
          this.apiKeyError = this.translate.instant('api_key_invalid');
        }
      },
      error: err => {
        this.apiKeyError = this.translate.instant('error_retry');
      }
  });
  }

  openActivationDialog($event: any) {
    console.log($event.checked);
    let message = '';
    let title = '';
    if ($event.checked) {
      title = this.translate.instant('activate_integration');
      message = this.translate.instant('activate_integration_message');
    } else {
      title = this.translate.instant('deactivate_integration');
      message = this.translate.instant('deactivate_integration_message');
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        title,
        message
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && result) {
        this.activateIntegration();
      } else {
        this.vivenuForm.controls.active.setValue(!this.vivenuForm.controls.active.value);
      }
    });
  }

  activateIntegration() {
    const active = this.vivenuForm.controls.active.value || false;
    this.vivenuService.activateVivenuIntegration(this.vivenu.id, active).subscribe((vivenu: any) => {
      console.log(vivenu);
      this.vivenu = vivenu;
      this.vivenuForm.patchValue(vivenu);
    });
  }

  openActivateSyncDialog($event: any, type: string) {
    let message = '';
    let title = '';
    if (type === 'vivenuCreateContact') {
      title = this.translate.instant('new_contacts');
    } else if (type === 'vivenuUpdateContact') {
      title = this.translate.instant('update_contacts');
    } else if (type === 'vivenuTransactionComplete') {
      title = this.translate.instant('completed_orders');
    } else if (type === 'vivenuCheckoutAborted') {
      title = this.translate.instant('aborted_orders');
    } else if (type === 'vivenuScan') {
      title = this.translate.instant('scans');
    }
    if ($event.checked) {
      title += this.translate.instant('activate_sync');
      message = this.translate.instant('activate_sync_message');
    } else {
      title += this.translate.instant('deactivate_sync');
      message = this.translate.instant('deactivate_sync_message');
    }
    if (type === 'anonymizeData') {
      title = this.translate.instant('anonymization');
      message = this.translate.instant('anonymization_message');
    }
    if (type === 'vivenuTicketsAsObjects') {
      title = this.translate.instant('tickets_as_custom_objects');
      message = this.translate.instant('tickets_as_objects_message');
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        title,
        message
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && result) {
        this.activateSync(type);
      } else {
        this.vivenuForm.get(type)!.setValue(!$event.checked);
      }
    });
  }

  activateSync(type: string) {
    let activeString = '';
    if (type !== 'vivenuTicketsAsObjects' && type !== 'anonymizeData') {
      activeString = 'Active';
    }
    const active = this.vivenuForm.get(type + activeString)!.value || false;
    this.vivenuService.activateVivenuSync(this.vivenu.id, active, type).subscribe({
      next: (vivenu: any) => {
        console.log(vivenu);
        this.vivenu = vivenu;
        this.vivenuForm.patchValue(vivenu);
      },
      error: () => {
        this.vivenuForm.get(type)!.setValue(!active);
        this.openSnackBar(this.translate.instant('error_retry'));
      }
    });
  }

  migrate(type: string) {
    let title = '';
    let message = '';
    if (type === 'contacts') {
      title = this.translate.instant('migrate_contacts_modal_title');
      message = this.translate.instant('migrate_contacts_modal_message');
    } else if (type === 'contacts_orders') {
      title = this.translate.instant('migrate_purchases_with_contacts_modal_title');
      message = this.translate.instant('migrate_purchases_with_contacts_modal_message');
    }else if (type === 'orders') {
      title = this.translate.instant('migrate_purchases_without_contacts_modal_title');
      message = this.translate.instant('migrate_purchases_without_contacts_modal_message');
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        title,
        message
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && result) {
        this.migrateData(type);
      }
    });
  }

  migrateData(type: string) {
    const timeRange = this.vivenuForm.get('migrationTimerange')!.value;
    this.vivenuService.migrateVivenuData(this.vivenu.id, type, timeRange!).subscribe({
      next: (vivenu: any) => {
        console.log(vivenu);
        if (vivenu && vivenu.status && vivenu.status === 202) {
          this.openSnackBar(this.translate.instant('migration_already_running'));
        } else {
          this.openSnackBar(this.translate.instant('migration_started_successfully'));
        }
        this.getMigrations();
      },
      error: () => {
        this.openSnackBar(this.translate.instant('error_retry'));
      }
    });
  }

  filterEntries(event: any, objectType: string, fieldType: string) {
    const filterValue = event.target.value;
    const filteredProperties: any[] = [];
    this.hubspotProperties[objectType].forEach((property: any) => {
      const label = property.name + ' (' + property.label + ')';
      if (label.toLowerCase().includes(filterValue.toLowerCase()) && property.fieldType === fieldType) {
        filteredProperties.push(property);
      }
    });
    this.filteredHubspotProperties[objectType] = filteredProperties;
  }

  createCustomFields() {
    const mapping: any = {
      'vivenu_ticket': this.vivenu.vivenuTicketsCustomObjectId || ''
    }
    this.fieldService.createFields('vivenu', this.hubspotCustomerId!, mapping).subscribe({
      next: () => {
        this.openSnackBar(this.translate.instant('fields_created_successfully'));
      },
      error: () => {
        this.openSnackBar(this.translate.instant('error_retry'));
      }
    });
  }

  saveFields() {
    console.log(this.vivenuFields);
    const fieldMapping = [];
    for (const field of this.vivenuFields) {
      fieldMapping.push({
        id: field.mappingId,
        fieldId: field.id,
        hubspotCustomerId: this.hubspotCustomerId,
        value: field.value,
        toExternal: field.toExternal,
        activeCreate: field.activeCreate,
        activeUpdate: field.activeUpdate
      });
    }
    this.fieldService.upsertFieldMappings('vivenu', this.hubspotCustomerId!, fieldMapping).subscribe({
      next: () => {
        this.getFields();
        this.openSnackBar(this.translate.instant('fields_saved_successfully'));
      },
      error: () => {
        this.openSnackBar(this.translate.instant('error_retry'));
      }
    });
  }

  updateSingleAttribute(attribute: string, value: any) {
    console.log({
      [attribute]: value
    });
    this.vivenuService.updateVivenuCustomer(this.vivenu.id, {
      [attribute]: value
    }).subscribe({
      next: (vivenu: any) => {
        this.openSnackBar(this.translate.instant('setting_saved_successfully'));
      },
      error: () => {
        this.openSnackBar(this.translate.instant('error_retry'));
      }
    });
  }

  saveOrderStages() {
    const orderPipeline = {
      type: 'order',
      id: this.vivenuForm.controls.orderPipeline.value,
      name: 'Vivenu Integration Pipeline',
      stages: [
        {
          name: 'Checkout aborted',
          id: this.vivenuForm.controls.orderStageAborted.value,
          type: 'aborted',
          order: 0
        },
        {
          name: 'Checkout pending',
          id: this.vivenuForm.controls.orderStagePending.value,
          type: 'pending',
          order: 1
        },
        {
          name: 'Checkout complete',
          id: this.vivenuForm.controls.orderStageComplete.value,
          type: 'complete',
          order: 2
        },
        {
          name: 'Processed',
          id: this.vivenuForm.controls.orderStageProcessed.value,
          type: 'processed',
          order: 3
        },
        {
          name: 'Canceled',
          id: this.vivenuForm.controls.orderStageCanceled.value,
          type: 'canceled',
          order: 4
        }
      ]
    };
    let pipelines = this.vivenu.pipelines;
    if (!pipelines || pipelines.length === 0) {
      pipelines = [];
      pipelines.push(orderPipeline);
    } else {
      const index = pipelines.findIndex((pipeline: any) => pipeline.type === 'order');
      pipelines[index] = orderPipeline;
    }
    this.updateSingleAttribute('pipelines', pipelines);
  }

  saveDealStages() {
    const dealPipeline = {
      type: 'deal',
      id: this.vivenuForm.controls.dealPipeline.value,
      name: 'Vivenu Integration Pipeline',
      stages: [
        {
          name: 'Checkout aborted',
          id: this.vivenuForm.controls.dealStageAborted.value,
          type: 'aborted',
          state: 'OPEN',
          order: 0
        },
        {
          name: 'Checkout pending',
          id: this.vivenuForm.controls.dealStagePending.value,
          type: 'pending',
          state: 'OPEN',
          order: 1
        },
        {
          name: 'Checkout complete',
          id: this.vivenuForm.controls.dealStageComplete.value,
          type: 'complete',
          state: 'CLOSED',
          order: 2
        },
        {
          name: 'Processed',
          id: this.vivenuForm.controls.dealStageProcessed.value,
          type: 'processed',
          state: 'CLOSED',
          order: 3
        },
        {
          name: 'Canceled',
          id: this.vivenuForm.controls.dealStageCanceled.value,
          type: 'canceled',
          state: 'CLOSED',
          order: 4
        }
      ]
    };
    let pipelines = this.vivenu.pipelines;
    if (!pipelines || pipelines.length === 0) {
      pipelines = [];
      pipelines.push(dealPipeline);
    } else {
      const index = pipelines.findIndex((pipeline: any) => pipeline.type === 'deal');
      pipelines[index] = dealPipeline;
    }
    this.updateSingleAttribute('pipelines', pipelines);
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, '', {
      duration: 4000,
    });
  }

  protected readonly parseInt = parseInt;
}
